import { useState } from "react";
import { ArrowDown, ArrowUpRight, Asterisk, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { archivePhotos, members, photoSrcSet, tour } from "../lib/tour";

const heroPhotos = archivePhotos.filter((photo) => photo.featured);

export function BandHero() {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [motionPaused, setMotionPaused] = useState(false);
  const photo = heroPhotos[photoIndex];

  return (
    <>
      <section id="home" className="hero shell" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow"><span className="status-dot" /> New lineup. Next chapter.</p>
          <p className="hero-season">Summer tour / {tour.year}</p>
          <h1 id="hero-title">Big sound.<br />Bigger<br /><em>summer.</em></h1>
          <p className="hero-description">
            {members.length} young musicians. One Lakewood Ranch House Band.
            Help us take the music on the road in {tour.year}.
          </p>
          <div className="hero-actions">
            <a className="button button-primary" href={tour.fundraiserUrl} target="_blank" rel="noopener noreferrer">
              Help us hit the road <ArrowUpRight aria-hidden="true" />
            </a>
            <a className="text-link" href="#band">Meet the band <ArrowDown aria-hidden="true" /></a>
          </div>
          <div className="hero-footnote"><span>Rooted in Lakewood Ranch, FL</span><span>Made for the stage</span></div>
        </div>

        <div className="hero-art">
          <div className="hero-stamp"><Asterisk aria-hidden="true" /><span>Live music.<br />Local roots.</span></div>
          <figure className="hero-photo" aria-label="Photos from previous House Band performances" aria-roledescription="carousel">
            <div className="hero-photo-frame">
              <img key={photo.id} src={photo.src} srcSet={photoSrcSet(photo)} sizes="(max-width: 800px) 100vw, 50vw" alt={photo.alt} fetchPriority="high" />
              <span className="photo-archive-label">House Band archive</span>
            </div>
            <figcaption>
              <div className="hero-photo-caption" aria-live="polite" aria-atomic="true">
                <strong>{photo.caption}</strong><span>{photo.date} / Previous lineup</span>
                {photo.sourceUrl && (
                  <a className="photo-source" href={photo.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {photo.credit ? `Photo: ${photo.credit}` : "View Instagram post"} <ArrowUpRight aria-hidden="true" />
                  </a>
                )}
              </div>
              <div className="photo-controls">
                <button className="icon-button" type="button" aria-label="Previous featured photo" onClick={() => setPhotoIndex((index) => (index - 1 + heroPhotos.length) % heroPhotos.length)}><ChevronLeft aria-hidden="true" /></button>
                <span className="photo-position" aria-label={`Photo ${photoIndex + 1} of ${heroPhotos.length}`}>{String(photoIndex + 1).padStart(2, "0")} / {String(heroPhotos.length).padStart(2, "0")}</span>
                <button className="icon-button" type="button" aria-label="Next featured photo" onClick={() => setPhotoIndex((index) => (index + 1) % heroPhotos.length)}><ChevronRight aria-hidden="true" /></button>
              </div>
            </figcaption>
          </figure>
          <div className="tour-sticker" aria-label={`Summer ${tour.year}, the next chapter`}>
            <span>Next stop</span><strong>SUMMER<br />'{String(tour.year).slice(-2)}</strong><ArrowUpRight aria-hidden="true" />
          </div>
        </div>
      </section>

      <div className="tour-ticker" data-paused={motionPaused}>
        <div className="ticker-window" aria-hidden="true">
          <div className="ticker-track">
            {[0, 1].map((copy) => (
              <div className="ticker-content" key={copy}>
                <span>Lakewood Ranch</span><Asterisk /><span>{members.length} musicians</span><Asterisk />
                <span>One House Band</span><Asterisk /><span>Summer {tour.year}</span><Asterisk />
              </div>
            ))}
          </div>
        </div>
        <button className="ticker-toggle" type="button" aria-label={motionPaused ? "Resume moving banner" : "Pause moving banner"} aria-pressed={motionPaused} onClick={() => setMotionPaused((paused) => !paused)}>
          {motionPaused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
        </button>
      </div>
    </>
  );
}
